import {RefObject, useEffect, useRef} from 'react'

// Maintains keyboard focus inside the dialog and prevents scrolling behind it.
export default function useDialog<T extends HTMLElement>(
  open: boolean,
  panel: RefObject<T>,
  onClose: () => void,
  canClose = true
) {
  const close = useRef(onClose)
  const closable = useRef(canClose)
  close.current = onClose
  closable.current = canClose

  useEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const focusable = () =>
      Array.from(
        panel.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex="0"]'
        ) ?? []
      ).filter((element) => element.getClientRects().length > 0)
    const initial =
      panel.current?.querySelector<HTMLElement>('[data-autofocus]') ??
      focusable()[0] ??
      panel.current
    initial?.focus()

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closable.current) {
        event.preventDefault()
        close.current()
      }
      if (event.key !== 'Tab') return
      const elements = focusable()
      const first = elements[0]
      const last = elements[elements.length - 1]
      if (!first) {
        event.preventDefault()
        panel.current?.focus()
      } else if (
        event.shiftKey &&
        (document.activeElement === first || !panel.current?.contains(document.activeElement))
      ) {
        event.preventDefault()
        last.focus()
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || !panel.current?.contains(document.activeElement))
      ) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
      if (previous?.isConnected) previous.focus()
    }
  }, [open, panel])
}
